import React, { useMemo } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import ProductCardSkeleton from '@components/organisms/catalog/components/skeleton-components/product-card-skeleton/product-card-skeleton';
import Button from '@components/atoms/button/button';
import Usp from '@components/organisms/usp/usp';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { CatalogContext } from '@context/catalog.context';
import useProductImpressionsTracking, {
  getImpressionTrackingIndex,
} from '@hooks/catalog-product/use-catalag-impressions-tracking';
import CategoryPageProductCard from '@components/catalog/catalog-page-product-card';
import ImpressionsTrackingWrapper from '@components/catalog/impressions-tracking-wrapper';
import useWindowSize from '@hooks/use-window-size';
import { useBucket } from '@context/bucket.context';
import isUSPBlockPositionMatched from '@helpers/utils/usp-block-matcher';
import dynamic from 'next/dynamic';
import useHighlightDutiesTooltip from '@hooks/catalog-product/use-highlight-duties-tooltip';
import { PLPInnerFeedContext } from '@context/plp-inner-feed.context';
import useRestoreCatalogScrollPosition from '@hooks/catalog-context/use-restore-catalog-scroll-position';
import styles from './product-search/product-search.module.scss';

const HighlightDutiesTooltip = dynamic(
  () => import('@components/catalog/highlight-duties-tooltip/highlight-duties-tooltip'),
  { ssr: false },
);

const HighlightTaxesBanner = dynamic(
  () => import('@components/catalog/highlight-duties-banner/highlight-duties-banner'),
  { ssr: false },
);

const PlpBreaker = dynamic(() => import('@components/catalog/plp-breaker/plp-breaker'), { ssr: false });

const ProductSection: React.FC = (): React.JSX.Element => {
  const { t } = useTranslation();
  const shouldShowLoadingAnimation = useContextSelector(CatalogContext, (v) => v.shouldShowLoadingAnimation);
  const areMySizesEnabled = useContextSelector(CatalogContext, (v) => v.areMySizesEnabled);
  const setMySizesState = useContextSelector(CatalogContext, (v) => v.setMySizesState);
  const areMySizesApplicable = useContextSelector(CatalogContext, (v) => v.areMySizesApplicable);
  const products = useContextSelector(CatalogContext, (v) => v.products);
  const pageCount = useContextSelector(CatalogContext, (v) => v.pageCount);
  const currentPage = useContextSelector(CatalogContext, (v) => v.currentPage);
  const resultsPerPage = useContextSelector(CatalogContext, (v) => v.resultsPerPage);
  const shouldRenderAllProducts = useContextSelector(CatalogContext, (v) => v.shouldRenderAllProducts);
  const isManualSwitchRef = useContextSelector(CatalogContext, (v) => v.isManualSwitchRef);
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const offset = queryResult?.paginationStats?.offset ?? 0;
  const showPlpInnerFeed = useContextSelector(PLPInnerFeedContext, (v) => v.showPlpInnerFeed);
  const displayAfterPosition = useContextSelector(PLPInnerFeedContext, (v) => v.displayAfterPosition);
  const { isWiderThanMd } = useWindowSize();
  const { isFeatureEnabled } = useBucket();
  const isCatalogUspEnabled = isFeatureEnabled((f) => !!f.isCatalogUspEnabled, true);

  const shouldShowDisableMySizesPopup: boolean = areMySizesEnabled && areMySizesApplicable && pageCount === currentPage;
  const isShowingUsp = isCatalogUspEnabled && currentPage === 1 && !showPlpInnerFeed;

  const { showTooltip, setShowTooltip, onRowVisibleCallback, shouldMountDutiesTooltip, showBanner, setShowBanner } =
    useHighlightDutiesTooltip(products);
  const { updateLatestProductIndex, rowCount } = useProductImpressionsTracking({ onRowVisibleCallback });

  const productsToRender = useMemo(
    () => (shouldRenderAllProducts ? products : products.slice(0, 12)),
    [shouldRenderAllProducts, products],
  );

  const { plpLastProductIdRef } = useRestoreCatalogScrollPosition();

  return (
    <>
      <div className={clsx(styles.catalog__resultContainer)}>
        {showBanner && <HighlightTaxesBanner handleClose={() => setShowBanner(false)} />}
        {shouldShowLoadingAnimation && (
          <ul className={styles.catalog__flexContainer}>
            {Array.from(Array(12).keys()).map((key) => (
              <li key={key}>
                <ProductCardSkeleton />
              </li>
            ))}
          </ul>
        )}
        {!shouldShowLoadingAnimation && (
          <>
            {productsToRender.length > 0 && (
              <ul className={styles.catalog__flexContainer}>
                {productsToRender.map((product: CatalogProduct, index: number) => {
                  const impressionTrackingIndex = getImpressionTrackingIndex({
                    index,
                    currentPage,
                    resultsPerPage,
                    isWiderThanMd,
                  });
                  const isFirstItemOnRow = (impressionTrackingIndex - 1) % rowCount === 0;
                  return (
                    <React.Fragment key={product.id}>
                      {isFirstItemOnRow ? (
                        <ImpressionsTrackingWrapper
                          onVisible={updateLatestProductIndex}
                          impressionTrackingIndex={impressionTrackingIndex}
                          productsOnRow={products.slice(index, index + rowCount)}
                        >
                          <CategoryPageProductCard
                            product={product}
                            index={index}
                            disableLazyLoad={index <= 3}
                            fetchpriority={index <= 3 ? 'high' : 'low'}
                            pageType="catalog"
                          />
                        </ImpressionsTrackingWrapper>
                      ) : (
                        <li>
                          <CategoryPageProductCard
                            product={product}
                            index={index}
                            disableLazyLoad={index <= 3}
                            fetchpriority={index <= 3 ? 'high' : 'low'}
                            pageType="catalog"
                          />
                        </li>
                      )}
                      {index === displayAfterPosition - offset - 1 && showPlpInnerFeed && (
                        <li className={styles.catalog__usp}>
                          <PlpBreaker plpLastProductIdRef={plpLastProductIdRef} />
                        </li>
                      )}
                      {/* USP BLOCK to be show only on the first page */}
                      {isShowingUsp && isUSPBlockPositionMatched(index, products.length, rowCount === 4 ? 8 : 6) && (
                        <li className={styles.catalog__usp}>
                          <Usp pageType="plp" />
                        </li>
                      )}
                      {/* END USP BLOCK */}
                    </React.Fragment>
                  );
                })}
              </ul>
            )}
            {/* 'No more results for my sizes' should only appear on last page */}
            {shouldShowDisableMySizesPopup && (
              <div className={styles.catalog__resultContainer__recommend}>
                <div className={styles.catalog__resultContainer__recommend__content}>
                  <div className={clsx('vc-text-xl', styles.catalog__resultContainer__recommend__title)}>
                    {t('PERSO.MORE_ITEM.TITLE')}
                  </div>
                  <p className="vc-text-m">{t('PERSO.MORE_ITEM.SUBTITLE_ALTERNATE')}</p>
                </div>
                <Button
                  className={styles.catalog__resultContainer__recommend__button}
                  onClick={() => {
                    isManualSwitchRef.current = true;
                    setMySizesState((prev) => ({ ...prev, enabled: false }), true);
                  }}
                >
                  {t('PERSO.MORE_ITEM.CTA')}
                </Button>
              </div>
            )}
            {!shouldShowDisableMySizesPopup && products.length === 0 && (
              <ul className={styles.catalog__noResultContainer}>{t('CATALOG.NO_RESULTS')}</ul>
            )}
          </>
        )}
      </div>
      {shouldMountDutiesTooltip && (
        <HighlightDutiesTooltip
          showTooltip={showTooltip}
          setShowTooltip={setShowTooltip}
        />
      )}
    </>
  );
};
export default ProductSection;
